'use client';

import { Tooltip, type TooltipTriggerProps } from '@base-ui/react/tooltip';
import clsx from 'clsx';
import type { CSSProperties, ReactNode, Ref } from 'react';

import '@repo/sds/components/SDSTooltip/SDSTooltip.scss';

export interface SDSTooltipProps {
  children: ReactNode;
  content: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Positioning
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;

  // Timing
  delay?: number;
  closeDelay?: number;

  // Styling
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
  contentStyle?: CSSProperties;

  // Arrow
  showArrow?: boolean;
  arrowClassName?: string;

  // Trigger
  renderTrigger?: TooltipTriggerProps['render'];

  dataTestId?: string;
  ref?: Ref<HTMLDivElement>;
}

type TooltipPopupProps = Pick<
  SDSTooltipProps,
  | 'align'
  | 'alignOffset'
  | 'arrowClassName'
  | 'content'
  | 'contentClassName'
  | 'contentStyle'
  | 'dataTestId'
  | 'ref'
  | 'showArrow'
  | 'side'
  | 'sideOffset'
>;

function TooltipPopup({
  align,
  alignOffset,
  arrowClassName,
  content,
  contentClassName,
  contentStyle,
  dataTestId,
  ref,
  showArrow,
  side,
  sideOffset,
}: TooltipPopupProps) {
  return (
    <Tooltip.Portal>
      <Tooltip.Positioner side={side} align={align} sideOffset={sideOffset} alignOffset={alignOffset}>
        <Tooltip.Popup
          ref={ref}
          className={clsx('SDSTooltip', contentClassName)}
          style={contentStyle}
          data-testid={dataTestId}
        >
          {showArrow && <Tooltip.Arrow className={clsx('SDSTooltip__arrow', arrowClassName)} />}
          {content}
        </Tooltip.Popup>
      </Tooltip.Positioner>
    </Tooltip.Portal>
  );
}

export function SDSTooltip({
  children,
  content,
  open,
  onOpenChange,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  alignOffset = 0,
  delay = 600,
  closeDelay = 0,
  className,
  contentClassName,
  style,
  contentStyle,
  showArrow = true,
  arrowClassName,
  renderTrigger,
  dataTestId,
  ref,
}: SDSTooltipProps) {
  return (
    <Tooltip.Provider delay={delay} closeDelay={closeDelay}>
      <Tooltip.Root open={open} onOpenChange={onOpenChange}>
        <Tooltip.Trigger className={className} style={style} render={renderTrigger}>
          {children}
        </Tooltip.Trigger>
        <TooltipPopup
          align={align}
          alignOffset={alignOffset}
          arrowClassName={arrowClassName}
          content={content}
          contentClassName={contentClassName}
          contentStyle={contentStyle}
          dataTestId={dataTestId}
          ref={ref}
          showArrow={showArrow}
          side={side}
          sideOffset={sideOffset}
        />
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
